import { useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { addSentryBreadcrumb } from './useSentryErrorCapture';

/**
 * Performance timing interface for tracking load metrics
 */
interface PerformanceTiming {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Loading phase enum for consistent tracking
 */
export enum LoadingPhase {
  APP_INITIALIZATION = 'app-initialization',
  I18N_LOADING = 'i18n-loading',
  AUTH_INITIALIZATION = 'auth-initialization',
  API_HEALTH_CHECK = 'api-health-check',
  FIREBASE_INITIALIZATION = 'firebase-initialization',
  SERVICE_WORKER_REGISTRATION = 'service-worker-registration',
  FIRST_RENDER = 'first-render'
}

/**
 * Centralized hook for managing loading metrics and Sentry logging
 */
export const useLoadingMetrics = () => {
  const transactions = useRef<Map<string, { setStatus: (status: string) => void; setData: (key: string, value: string | number | boolean) => void; finish: () => void }>>(new Map());
  const timings = useRef<Map<string, PerformanceTiming>>(new Map());
  const isInitialized = useRef(false);
  const webVitalsRef = useRef<{ fcp?: number; lcp?: number; cls?: number; fid?: number }>({});

  /**
   * Initialize Web Vitals and performance observers
   */
  const initializeWebVitals = useCallback(() => {
    if (isInitialized.current || typeof window === 'undefined') return;
    
    try {
      // Track First Contentful Paint
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'paint' && entry.name === 'first-contentful-paint') {
            webVitalsRef.current.fcp = entry.startTime;
            Sentry.setMeasurement('fcp', entry.startTime, 'millisecond');
            addSentryBreadcrumb(
              `First Contentful Paint: ${entry.startTime.toFixed(2)}ms`,
              'performance',
              { fcp: entry.startTime }
            );
          }
        }
      });
      
      observer.observe({ entryTypes: ['paint'] });
      
      // Track Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          webVitalsRef.current.lcp = lastEntry.startTime;
          Sentry.setMeasurement('lcp', lastEntry.startTime, 'millisecond');
          addSentryBreadcrumb(
            `Largest Contentful Paint: ${lastEntry.startTime.toFixed(2)}ms`,
            'performance',
            { lcp: lastEntry.startTime }
          );
        }
      });
      
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      
      // Track Cumulative Layout Shift
       const clsObserver = new PerformanceObserver((list) => {
         let clsValue = 0;
         for (const entry of list.getEntries()) {
           const layoutShiftEntry = entry as PerformanceEntry & {
             hadRecentInput?: boolean;
             value?: number;
           };
           if (!layoutShiftEntry.hadRecentInput) {
             clsValue += layoutShiftEntry.value || 0;
           }
         }
         webVitalsRef.current.cls = clsValue;
         Sentry.setMeasurement('cls', clsValue, 'ratio');
       });
      
      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Layout shift not supported in all browsers
      }
      
      // Track First Input Delay
       const fidObserver = new PerformanceObserver((list) => {
         for (const entry of list.getEntries()) {
           const firstInputEntry = entry as PerformanceEntry & {
             processingStart?: number;
           };
           const fid = (firstInputEntry.processingStart || 0) - entry.startTime;
           webVitalsRef.current.fid = fid;
           Sentry.setMeasurement('fid', fid, 'millisecond');
           addSentryBreadcrumb(
             `First Input Delay: ${fid.toFixed(2)}ms`,
             'performance',
             { fid }
           );
         }
       });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // First input not supported in all browsers
      }
      
      isInitialized.current = true;
    } catch (error) {
      console.warn('Failed to initialize Web Vitals:', error);
    }
  }, []);

  /**
   * Start a performance transaction
   */
  const startTransaction = useCallback((phase: LoadingPhase, metadata?: Record<string, unknown>) => {
    try {
      const transaction = Sentry.startSpan({
        name: phase,
        op: 'navigation.loading'
      }, () => {
        // Transaction will be automatically finished when span ends
        return null;
      });
      
      // For compatibility, create a simple transaction-like object
      const transactionObj = {
        setStatus: (status: string) => {
          Sentry.setTag('transactionStatus', status);
        },
        setData: (key: string, value: string | number | boolean) => {
           Sentry.setTag(`transaction_${key}`, String(value));
        },
        finish: () => {
          // Span will be finished automatically
        }
      };
      
      // Set initial tags
      Sentry.setTag('loadingPhase', phase);
      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
           Sentry.setTag(`metadata_${key}`, String(value));
        });
      }
      
      transactions.current.set(phase, transactionObj);
      
      // Start timing
      timings.current.set(phase, {
        name: phase,
        startTime: performance.now(),
        metadata
      });
      
      addSentryBreadcrumb(
        `Started ${phase}`,
        'loading',
        { phase, ...metadata }
      );
      
      return transactionObj;
    } catch (error) {
      console.warn(`Failed to start transaction for ${phase}:`, error);
      return null;
    }
  }, []);

  /**
   * Finish a performance transaction
   */
  const finishTransaction = useCallback((phase: LoadingPhase, status: 'ok' | 'error' = 'ok', metadata?: Record<string, unknown>) => {
    try {
      const transaction = transactions.current.get(phase);
      const timing = timings.current.get(phase);
      
      if (transaction && timing) {
        const endTime = performance.now();
        const duration = endTime - timing.startTime;
        
        // Update timing
        timing.endTime = endTime;
        timing.duration = duration;
        
        // Set transaction data
        transaction.setStatus(status);
        transaction.setData('duration', duration);
        if (metadata) {
          Object.entries(metadata).forEach(([key, value]) => {
            transaction.setData(key, String(value));
          });
        }
        
        // Finish transaction
        transaction.finish();
        
        // Set measurement in Sentry
        Sentry.setMeasurement(phase, duration, 'millisecond');
        
        addSentryBreadcrumb(
          `Finished ${phase}: ${duration.toFixed(2)}ms`,
          'loading',
          { phase, duration, status, ...metadata }
        );
        
        // Clean up
        transactions.current.delete(phase);
      }
    } catch (error) {
      console.warn(`Failed to finish transaction for ${phase}:`, error);
    }
  }, []);

  /**
   * Log a loading event without transaction
   */
  const logLoadingEvent = useCallback((event: string, data?: Record<string, unknown>) => {
    addSentryBreadcrumb(event, 'loading', data);
    
    // Also log to console in development
    if (import.meta.env.DEV) {
      console.log(`[Loading] ${event}`, data);
    }
  }, []);

  /**
   * Log an error during loading
   */
  const logLoadingError = useCallback((phase: LoadingPhase, error: Error, context?: Record<string, unknown>) => {
    Sentry.withScope((scope) => {
      scope.setTag('loadingPhase', phase);
      scope.setLevel('error');
      scope.setContext('loadingError', {
        phase,
        ...context
      });
      
      Sentry.captureException(error);
    });
    
    // Finish transaction with error status
    finishTransaction(phase, 'error', { error: error.message, ...context });
  }, [finishTransaction]);

  /**
   * Get current timing data
   */
  const getTimings = useCallback(() => {
    return Array.from(timings.current.values());
  }, []);

  /**
   * Log performance summary
   */
  const logPerformanceSummary = useCallback(() => {
    const allTimings = getTimings();
    const completedTimings = allTimings.filter(t => t.duration !== undefined);
    
    if (completedTimings.length > 0) {
      const totalLoadTime = completedTimings.reduce((sum, t) => sum + (t.duration || 0), 0);
      
      Sentry.setMeasurement('total-load-time', totalLoadTime, 'millisecond');
      
      addSentryBreadcrumb(
        `Performance Summary - Total: ${totalLoadTime.toFixed(2)}ms`,
        'performance',
        {
          totalLoadTime,
          phases: completedTimings.length,
          timings: completedTimings.map(t => ({
            phase: t.name,
            duration: t.duration
          }))
        }
      );
    }
  }, [getTimings]);

  // Initialize Web Vitals on mount
  useEffect(() => {
    initializeWebVitals();
  }, [initializeWebVitals]);

  const getWebVitals = useCallback(() => {
    return {
      fcp: webVitalsRef.current.fcp,
      lcp: webVitalsRef.current.lcp,
      cls: webVitalsRef.current.cls,
      fid: webVitalsRef.current.fid
    };
  }, []);

  const getLoadingPhases = useCallback(() => {
    const phases: Record<string, { duration?: number; success?: boolean; error?: string }> = {};
    
    timings.current.forEach((timing, phase) => {
      phases[phase] = {
        duration: timing.duration,
        success: timing.endTime ? true : undefined,
        error: undefined
      };
    });
    
    return phases;
  }, []);

  return {
    startTransaction,
    finishTransaction,
    logLoadingEvent,
    logLoadingError,
    getTimings,
    logPerformanceSummary,
    getWebVitals,
    getLoadingPhases,
    LoadingPhase
  };
};

/**
 * Hook to automatically track a loading phase in a component
 */
export const useLoadingPhaseTracking = (phase: LoadingPhase, metadata?: Record<string, unknown>) => {
  const { startTransaction, finishTransaction } = useLoadingMetrics();
  
  useEffect(() => {
    const transaction = startTransaction(phase, metadata);
    
    return () => {
      if (transaction) {
        finishTransaction(phase);
      }
    };
  }, [startTransaction, finishTransaction, phase, metadata]);
};

/**
 * Utility function to measure async operations
 */
export const measureAsyncOperation = async <T>(
  phase: LoadingPhase,
  operation: () => Promise<T>,
  metadata?: Record<string, unknown>
): Promise<T> => {
  const startTime = performance.now();
  
  try {
    addSentryBreadcrumb(`Starting ${phase}`, 'loading', metadata);
    
    const result = await operation();
    
    const duration = performance.now() - startTime;
    Sentry.setMeasurement(phase, duration, 'millisecond');
    
    addSentryBreadcrumb(
      `Completed ${phase}: ${duration.toFixed(2)}ms`,
      'loading',
      { duration, success: true, ...metadata }
    );
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    Sentry.withScope((scope) => {
      scope.setTag('loadingPhase', phase);
      scope.setLevel('error');
      scope.setContext('asyncOperation', {
        phase,
        duration,
        ...metadata
      });
      
      Sentry.captureException(error);
    });
    
    addSentryBreadcrumb(
      `Failed ${phase}: ${duration.toFixed(2)}ms`,
      'loading',
      { duration, success: false, error: error instanceof Error ? error.message : String(error), ...metadata }
    );
    
    throw error;
  }
};